import { useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCopy, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { parseCidr, parseIpMask, type SubnetInfo } from '../lib/ipSubnet';
import { ToolHeader } from '../components/ToolHeader';

type Mode = 'cidr' | 'ipmask';
type Result = SubnetInfo | { error: string };

/**
 * `/tools/network/ip-subnet` — IPv4 subnet calculator.
 *
 * Two equivalent input modes (CIDR or IP+mask) drive the same
 * `SubnetInfo` block. The bit-level visualizer at the bottom splits
 * the network and host portions with colour, which is by far the
 * most-asked-for affordance from anyone teaching subnetting.
 */
export default function IpSubnetTool(): JSX.Element {
  const [mode, setMode] = useState<Mode>('cidr');
  const [cidr, setCidr] = useState('192.168.1.0/24');
  const [ip, setIp] = useState('192.168.1.0');
  const [mask, setMask] = useState('255.255.255.0');

  const result: Result = useMemo(() => {
    if (mode === 'cidr') return parseCidr(cidr);
    return parseIpMask(ip, mask);
  }, [mode, cidr, ip, mask]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ToolHeader
        toolId="ip-subnet"
        category="network"
        description="CIDR ↔ IP+掩码 双向、网络 / 广播 / 主机范围、二进制位拆色。私有地址识别覆盖 RFC 1918 / CGNAT / link-local / loopback。"
      />

      <section className="card-pad flex flex-col gap-2">
        <div className="inline-flex rounded border border-line bg-bg-card overflow-hidden text-xs w-fit">
          {(['cidr', 'ipmask'] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={clsx(
                'px-3 py-1',
                mode === m
                  ? 'bg-accent text-white'
                  : 'text-ink-muted hover:bg-bg-soft hover:text-accent',
              )}
              onClick={() => setMode(m)}
            >
              {m === 'cidr' ? 'CIDR' : 'IP + 掩码'}
            </button>
          ))}
        </div>

        {mode === 'cidr' ? (
          <input
            className="input font-mono"
            placeholder="192.168.1.0/24"
            value={cidr}
            onChange={(e) => setCidr(e.target.value)}
          />
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs text-ink-muted">
              IP
              <input
                className="input font-mono"
                placeholder="192.168.1.0"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-muted">
              掩码
              <input
                className="input font-mono"
                placeholder="255.255.255.0"
                value={mask}
                onChange={(e) => setMask(e.target.value)}
              />
            </label>
          </div>
        )}

        {'error' in result && (
          <p className="inline-flex items-center gap-1 text-sm text-rose-600 dark:text-rose-400">
            <XCircle className="h-3.5 w-3.5" />
            {result.error}
          </p>
        )}
      </section>

      {!('error' in result) && <ResultBlock info={result} />}
    </div>
  );
}

function ResultBlock({ info }: { info: SubnetInfo }): JSX.Element {
  return (
    <>
      <section className="card-pad">
        <header className="mb-2 text-[11px] uppercase tracking-wide text-ink-dim">
          子网信息
        </header>
        <dl className="grid grid-cols-1 gap-x-3 gap-y-1 sm:grid-cols-[8em_1fr] text-[12.5px]">
          <FieldRow label="CIDR" value={info.cidr} />
          <FieldRow label="网络地址" value={info.network} />
          <FieldRow label="广播地址" value={info.broadcast} />
          <FieldRow label="子网掩码" value={info.mask} />
          <FieldRow label="通配符掩码" value={info.wildcard} />
          <FieldRow
            label="主机范围"
            value={info.hostCount === 0 ? '—' : `${info.hostMin} – ${info.hostMax}`}
          />
          <FieldRow label="主机数" value={info.hostCount.toLocaleString()} />
          <FieldRow
            label="地址类"
            value={`${info.addrClass} 类${info.isPrivate ? '（私有）' : '（公网）'}`}
          />
        </dl>
      </section>

      <section className="card-pad">
        <header className="mb-2 text-[11px] uppercase tracking-wide text-ink-dim">
          二进制（网络位 {info.binary.networkBits} / 主机位 {info.binary.hostBits}）
        </header>
        <BinaryView binStr={info.binary.binStr} networkBits={info.binary.networkBits} />
      </section>
    </>
  );
}

function BinaryView({
  binStr,
  networkBits,
}: {
  binStr: string;
  networkBits: number;
}): JSX.Element {
  // Group as 8-bit octets to match the dotted-quad mental model.
  const octets: string[] = [
    binStr.slice(0, 8),
    binStr.slice(8, 16),
    binStr.slice(16, 24),
    binStr.slice(24, 32),
  ];
  return (
    <div className="overflow-x-auto font-mono text-[12px]">
      <div className="flex flex-wrap items-baseline gap-x-1">
        {octets.map((octet, oi) => (
          <span key={oi} className="inline-flex">
            {octet.split('').map((bit, bi) => {
              const globalIdx = oi * 8 + bi;
              const isNetwork = globalIdx < networkBits;
              return (
                <span
                  key={bi}
                  className={clsx(
                    'inline-block w-3 text-center',
                    isNetwork ? 'text-accent' : 'text-ink-muted',
                  )}
                >
                  {bit}
                </span>
              );
            })}
            {oi < 3 && <span className="px-0.5 text-ink-dim">.</span>}
          </span>
        ))}
      </div>
      <div className="mt-1 text-[11px] text-ink-dim">
        <span className="text-accent">█</span> 网络位 ·{' '}
        <span className="text-ink-muted">█</span> 主机位
      </div>
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <>
      <dt className="text-ink-dim">{label}</dt>
      <dd className="flex items-center gap-2 font-mono text-ink min-w-0">
        <span className="min-w-0 flex-1 break-all">{value}</span>
        <CopyButton value={value} />
      </dd>
    </>
  );
}

function CopyButton({ value }: { value: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1000);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded p-1 text-ink-dim hover:bg-bg-soft hover:text-accent"
      title="复制"
    >
      {done ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
      ) : (
        <ClipboardCopy className="h-3 w-3" />
      )}
    </button>
  );
}
